import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

const SVG_SIZE = 450;

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '4px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '16px',
  },
  chartContainer: {
    display: 'flex',
    justifyContent: 'center',
  },
  breadcrumb: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '12px',
    color: '#6b7280',
    marginBottom: '12px',
    minHeight: '20px',
  },
  breadcrumbSegment: {
    color: '#3b82f6',
    cursor: 'pointer',
    fontWeight: 500,
  },
  breadcrumbSeparator: {
    color: '#d1d5db',
    userSelect: 'none',
  },
  breadcrumbCurrent: {
    color: '#1f2937',
    fontWeight: 600,
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '10px 14px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    maxWidth: '220px',
  },
  emptyState: {
    color: '#6b7280',
    textAlign: 'center',
    padding: '40px 0',
  },
};

/**
 * Count total leaf nodes (addresses) under a hierarchy node.
 */
function countLeaves(node) {
  if (!node.children || node.children.length === 0) return 1;
  return node.children.reduce((sum, c) => sum + countLeaves(c), 0);
}

/**
 * Build the breadcrumb path from root to a given d3 hierarchy node.
 */
function getAncestors(node) {
  const path = [];
  let current = node;
  while (current) {
    path.unshift(current);
    current = current.parent;
  }
  return path;
}

export default function SunburstChart({ data }) {
  const svgRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);
  const [breadcrumb, setBreadcrumb] = useState([]);
  const currentFocusRef = useRef(null);

  useEffect(() => {
    if (!data || !data.children || data.children.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const radius = SVG_SIZE / 2;

    svg
      .attr('viewBox', `0 0 ${SVG_SIZE} ${SVG_SIZE}`)
      .attr('width', '100%')
      .attr('height', '100%')
      .style('max-width', `${SVG_SIZE}px`);

    const g = svg.append('g').attr('transform', `translate(${radius}, ${radius})`);

    // Build hierarchy with price-based values
    const root = d3
      .hierarchy(data)
      .sum((d) => (d.children ? 0 : d.price || 0))
      .sort((a, b) => b.value - a.value);

    const partitionLayout = d3.partition().size([2 * Math.PI, radius]);
    partitionLayout(root);

    // Color mapping: state-level gets distinct Tableau10 colors,
    // city & address levels get progressively lighter shades
    const stateNodes = root.children || [];
    const stateColorMap = {};
    stateNodes.forEach((stateNode, i) => {
      stateColorMap[stateNode.data.name] = d3.schemeTableau10[i % 10];
    });

    function getNodeColor(d) {
      if (d.depth === 0) return '#f3f4f6';

      // Find the state ancestor
      let stateAncestor = d;
      while (stateAncestor.depth > 1) {
        stateAncestor = stateAncestor.parent;
      }
      const baseColor = stateColorMap[stateAncestor.data.name] || '#6b7280';

      if (d.depth === 1) {
        // State level: full color
        return baseColor;
      } else if (d.depth === 2) {
        // City level: lighter
        return d3.interpolate(baseColor, '#ffffff')(0.35);
      } else {
        // Address level: even lighter
        return d3.interpolate(baseColor, '#ffffff')(0.6);
      }
    }

    // Arc generator
    const arcGen = d3
      .arc()
      .startAngle((d) => d.x0)
      .endAngle((d) => d.x1)
      .innerRadius((d) => d.y0)
      .outerRadius((d) => d.y1 - 1)
      .padAngle(0.005)
      .padRadius(radius / 2);

    // Store the current view for transitions
    const currentView = { x0: 0, x1: 2 * Math.PI, y0: 0, y1: radius };
    currentFocusRef.current = root;

    // Helper to clamp arcs during transitions
    function arcVisible(d) {
      return d.y1 > d.y0 && d.x1 > d.x0 && d.y0 < radius && d.x1 - d.x0 > 0.001;
    }

    // Draw arcs for all descendants (skip root - it becomes the center circle)
    const paths = g
      .selectAll('path.arc-segment')
      .data(root.descendants().filter((d) => d.depth > 0))
      .enter()
      .append('path')
      .attr('class', 'arc-segment')
      .attr('d', arcGen)
      .attr('fill', (d) => getNodeColor(d))
      .attr('fill-opacity', (d) => (arcVisible(d) ? 0.85 : 0))
      .attr('stroke', '#fff')
      .attr('stroke-width', 0.5)
      .style('cursor', 'pointer')
      .on('mouseenter', function (event, d) {
        d3.select(this).transition().duration(150).attr('fill-opacity', 1);
        const isLeaf = !d.children;
        const addressCount = isLeaf ? 1 : countLeaves(d.data);
        setTooltip({
          x: event.clientX + 14,
          y: event.clientY - 14,
          name: d.data.name,
          value: d.value,
          depth: d.depth,
          isLeaf,
          addressCount,
          price: isLeaf ? d.data.price : null,
          changePercent: isLeaf ? d.data.changePercent : null,
        });
      })
      .on('mousemove', function (event, d) {
        const isLeaf = !d.children;
        const addressCount = isLeaf ? 1 : countLeaves(d.data);
        setTooltip({
          x: event.clientX + 14,
          y: event.clientY - 14,
          name: d.data.name,
          value: d.value,
          depth: d.depth,
          isLeaf,
          addressCount,
          price: isLeaf ? d.data.price : null,
          changePercent: isLeaf ? d.data.changePercent : null,
        });
      })
      .on('mouseleave', function () {
        d3.select(this).transition().duration(150).attr('fill-opacity', 0.85);
        setTooltip(null);
      })
      .on('click', function (event, d) {
        if (!d.children) return; // no drill-down on leaves
        zoomTo(d);
      });

    // Center circle for zoom-out
    const centerCircle = g
      .append('circle')
      .attr('r', () => root.y1 - root.y0 || radius * 0.18)
      .attr('fill', '#fafbfc')
      .attr('stroke', '#e5e7eb')
      .attr('stroke-width', 1)
      .style('cursor', 'pointer')
      .on('click', function () {
        const current = currentFocusRef.current;
        if (current && current.parent) {
          zoomTo(current.parent);
        }
      });

    // Center label
    const centerLabel = g
      .append('text')
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'central')
      .attr('font-size', '11px')
      .attr('fill', '#6b7280')
      .attr('pointer-events', 'none')
      .text(data.name || 'All');

    // Initialize breadcrumb
    setBreadcrumb([{ name: data.name || 'All', node: root }]);

    function zoomTo(target) {
      currentFocusRef.current = target;

      // Update breadcrumb
      const ancestors = getAncestors(target);
      setBreadcrumb(
        ancestors.map((a) => ({
          name: a.data.name || 'All',
          node: a,
        }))
      );

      // Update center label
      centerLabel.text(target.data.name || 'All');

      // Transition arcs
      const targetX0 = target.x0;
      const targetX1 = target.x1;
      const targetY0 = target.y0;

      const xScale = d3
        .scaleLinear()
        .domain([targetX0, targetX1])
        .range([0, 2 * Math.PI]);

      const yScale = d3
        .scaleLinear()
        .domain([targetY0, radius])
        .range([0, radius]);

      const transitionArc = d3
        .arc()
        .startAngle((d) => Math.max(0, Math.min(2 * Math.PI, xScale(d.x0))))
        .endAngle((d) => Math.max(0, Math.min(2 * Math.PI, xScale(d.x1))))
        .innerRadius((d) => Math.max(0, yScale(d.y0)))
        .outerRadius((d) => Math.max(0, yScale(d.y1) - 1))
        .padAngle(0.005)
        .padRadius(radius / 2);

      paths
        .transition()
        .duration(600)
        .attr('d', transitionArc)
        .attr('fill-opacity', function (d) {
          // Only show arcs that are descendants of the target
          const isDescendant =
            d.x0 >= targetX0 && d.x1 <= targetX1 && d.y0 >= targetY0;
          const newArc = transitionArc(d);
          return isDescendant && newArc ? 0.85 : 0;
        });

      // Update center circle radius
      centerCircle
        .transition()
        .duration(600)
        .attr('r', yScale(target.y1) - 1 || radius * 0.18);
    }

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data]);

  // Empty state
  if (!data || !data.children || data.children.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>Pricing Hierarchy</div>
        <div style={styles.subtitle}>
          Drill down: State &rarr; City &rarr; Address
        </div>
        <div style={styles.emptyState}>No hierarchy data available</div>
      </div>
    );
  }

  const depthLabel = (depth) => {
    if (depth === 1) return 'State';
    if (depth === 2) return 'City';
    return 'Address';
  };

  return (
    <div style={styles.card}>
      <div style={styles.title}>Pricing Hierarchy</div>
      <div style={styles.subtitle}>
        Drill down: State &rarr; City &rarr; Address
      </div>

      {/* Breadcrumb trail */}
      <div style={styles.breadcrumb}>
        {breadcrumb.map((crumb, i) => {
          const isLast = i === breadcrumb.length - 1;
          return (
            <React.Fragment key={i}>
              {i > 0 && <span style={styles.breadcrumbSeparator}>&rsaquo;</span>}
              {isLast ? (
                <span style={styles.breadcrumbCurrent}>{crumb.name}</span>
              ) : (
                <span
                  style={styles.breadcrumbSegment}
                  onClick={() => {
                    // Trigger zoom to this ancestor node
                    // We re-dispatch by simulating the click logic
                    if (svgRef.current && crumb.node) {
                      const svg = d3.select(svgRef.current);
                      // Find matching path and trigger zoom
                      svg.selectAll('path.arc-segment').each(function (d) {
                        if (d === crumb.node) {
                          d3.select(this).dispatch('click');
                        }
                      });
                      // If clicking root, simulate center circle click
                      if (crumb.node.depth === 0) {
                        svg.select('circle').dispatch('click');
                      }
                    }
                  }}
                >
                  {crumb.name}
                </span>
              )}
            </React.Fragment>
          );
        })}
      </div>

      <div style={styles.chartContainer}>
        <svg ref={svgRef} />
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div
            style={{
              fontWeight: 600,
              color: '#1f2937',
              marginBottom: '6px',
              fontSize: '13px',
            }}
          >
            {tooltip.name}
            <span
              style={{
                fontWeight: 400,
                color: '#9ca3af',
                fontSize: '11px',
                marginLeft: '6px',
              }}
            >
              {depthLabel(tooltip.depth)}
            </span>
          </div>
          {tooltip.isLeaf && tooltip.price != null && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: '12px',
                marginBottom: '2px',
              }}
            >
              <span style={{ color: '#6b7280' }}>pest365</span>
              <span style={{ fontWeight: 600, color: '#1f2937' }}>
                ${tooltip.price.toFixed(2)}
              </span>
            </div>
          )}
          {!tooltip.isLeaf && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: '12px',
                marginBottom: '2px',
              }}
            >
              <span style={{ color: '#6b7280' }}>Total Price Sum</span>
              <span style={{ fontWeight: 600, color: '#1f2937' }}>
                ${tooltip.value.toFixed(2)}
              </span>
            </div>
          )}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '12px',
              marginBottom: '2px',
            }}
          >
            <span style={{ color: '#6b7280' }}>Addresses</span>
            <span style={{ fontWeight: 600, color: '#1f2937' }}>
              {tooltip.addressCount}
            </span>
          </div>
          {tooltip.isLeaf && tooltip.changePercent != null && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: '12px',
              }}
            >
              <span style={{ color: '#6b7280' }}>Change</span>
              <span
                style={{
                  fontWeight: 600,
                  color:
                    tooltip.changePercent > 0
                      ? '#16a34a'
                      : tooltip.changePercent < 0
                        ? '#dc2626'
                        : '#6b7280',
                }}
              >
                {tooltip.changePercent > 0 ? '+' : ''}
                {tooltip.changePercent.toFixed(1)}%
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
